import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, PointerEvent } from "react";
import { fitGalaxyView, panGalaxyView, revealGalaxyPoint, zoomGalaxyView } from "./galaxy-viewport";
import type { GalaxyView, GalaxyViewportSize } from "./galaxy-viewport";
import type { Point } from "./game/geometry";

interface PointerPosition extends Point { startX: number; startY: number }

export function useGalaxyViewport(onInteraction: () => void) {
  const viewport = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<GalaxyView>({ x: 0, y: 0, scale: 1 });
  const [size, setSize] = useState<GalaxyViewportSize>({ width: 0, height: 0 });
  const current = useRef(view);
  const dimensions = useRef(size);
  const pointers = useRef(new Map<number, PointerPosition>());
  const dragged = useRef(false);
  const suppressClick = useRef(false);
  const interaction = useRef(onInteraction);
  interaction.current = onInteraction;
  const update = useCallback((next: GalaxyView) => {
    current.current = next;
    setView(next);
  }, []);
  const zoom = useCallback((factor: number, anchor?: Point) => {
    const bounds = dimensions.current;
    update(zoomGalaxyView(current.current, bounds, factor, anchor ?? { x: bounds.width / 2, y: bounds.height / 2 }));
  }, [update]);
  const reset = useCallback(() => update(fitGalaxyView(dimensions.current)), [update]);
  const reveal = useCallback((point: Point) => {
    const topLeft = revealGalaxyPoint(current.current, dimensions.current, { x: point.x - 75, y: point.y - 60 });
    update(revealGalaxyPoint(topLeft, dimensions.current, { x: point.x + 75, y: point.y + 60 }));
  }, [update]);

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const resize = () => {
      const next = { width: element.clientWidth, height: element.clientHeight };
      dimensions.current = next;
      setSize(next);
      update(fitGalaxyView(next));
    };
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      if (event.target instanceof Element && event.target.closest("[data-galaxy-ui]")) return;
      const rect = element.getBoundingClientRect();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
      const delta = Math.max(-1000, Math.min(1000, event.deltaY * unit));
      interaction.current();
      zoom(Math.exp(-delta * .002), { x: event.clientX - rect.left, y: event.clientY - rect.top });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    element.addEventListener("wheel", wheel, { passive: false });
    resize();
    return () => {
      observer.disconnect();
      element.removeEventListener("wheel", wheel);
    };
  }, [update, zoom]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || (event.target instanceof Element && event.target.closest("[data-galaxy-ui]"))) return;
    if (!pointers.current.size) {
      dragged.current = false;
      suppressClick.current = false;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY });
    if (pointers.current.size > 1) {
      suppressClick.current = true;
      dragged.current = true;
      interaction.current();
      for (const id of pointers.current.keys()) event.currentTarget.setPointerCapture(id);
    }
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const before = [...pointers.current.values()];
    pointers.current.set(event.pointerId, { ...previous, x: event.clientX, y: event.clientY });
    const after = [...pointers.current.values()];
    if (after.length === 1) {
      const started = !dragged.current;
      if (started && Math.hypot(event.clientX - previous.startX, event.clientY - previous.startY) < 6) return;
      if (started) {
        dragged.current = true;
        suppressClick.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        interaction.current();
      }
      update(panGalaxyView(current.current, dimensions.current, {
        x: event.clientX - (started ? previous.startX : previous.x),
        y: event.clientY - (started ? previous.startY : previous.y),
      }));
    } else {
      const [a, b] = before;
      const [c, d] = after;
      if (!a || !b || !c || !d) return;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (distance < 1) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const anchor = { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top };
      const next = zoomGalaxyView(current.current, dimensions.current, Math.hypot(c.x - d.x, c.y - d.y) / distance, anchor);
      update(panGalaxyView(next, dimensions.current, { x: (c.x + d.x - a.x - b.x) / 2, y: (c.y + d.y - a.y - b.y) / 2 }));
    }
    event.preventDefault();
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    for (const [id, point] of pointers.current) pointers.current.set(id, { ...point, startX: point.x, startY: point.y });
  };
  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (event.detail === 0 || !suppressClick.current || (event.target instanceof Element && event.target.closest("[data-galaxy-ui]"))) return;
    event.preventDefault();
    event.stopPropagation();
  };
  return { viewport, view, size, zoom, reset, reveal, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture } };
}
